import { useCustomerCategories } from '@/features/settings';
import { customerCategoryLabel } from '@erp/shared';
import { PriceGroupFilter } from '@/features/settings';
import { CatalogueSelect } from '@/components/common/catalogue-select';
import { subdivisionOptions } from '@/features/address-resolution';
import {
  CUSTOMER_INDUSTRY_CODE_MAX_LENGTH,
  LEAD_SOURCES,
  LEAD_STATUSES,
  PARTNER_RISK_STATUSES,
  SUPPLIER_RATINGS,
  type PartnerType,
} from '@erp/shared';
import { Filter, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  EMPTY_PARTNER_FILTERS,
  countActiveFilters,
  type PartnerFiltersValue,
} from '@/features/partners/lib/partner-filters-value';

const STATUSES = ['ACTIVE', 'PROSPECT', 'BLOCKED', 'INACTIVE'] as const;
const VAT_STATUSES = [
  'UNCHECKED',
  'VALID',
  'INVALID',
  'NOT_CHECKABLE',
  'SERVICE_UNAVAILABLE',
] as const;

export interface PartnerFiltersProps {
  type: PartnerType;
  value: PartnerFiltersValue;
  onChange: (value: PartnerFiltersValue) => void;
  availableTags: { value: string; count: number }[];
}

function toggle(list: string[], entry: string): string[] {
  return list.includes(entry) ? list.filter((item) => item !== entry) : [...list, entry];
}

export function PartnerFilters({
  type,
  value,
  onChange,
  availableTags,
}: PartnerFiltersProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const categories = useCustomerCategories();
  const activeCount = countActiveFilters(value);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <PriceGroupFilter
        value={value.priceGroupIds}
        onChange={(priceGroupIds) => onChange({ ...value, priceGroupIds })}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4" aria-hidden="true" />
            {t('partners.filters.status')}
            {value.status.length > 0 ? (
              <Badge variant="secondary">{value.status.length}</Badge>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{t('partners.filters.status')}</DropdownMenuLabel>
          {STATUSES.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={value.status.includes(status)}
              onCheckedChange={() => onChange({ ...value, status: toggle(value.status, status) })}
            >
              {t(`partners.statuses.${status}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4" aria-hidden="true" />
            {t('partners.filters.industry')}
            {value.industry.length > 0 ? (
              <Badge variant="secondary">{value.industry.length}</Badge>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
          <DropdownMenuLabel>{t('partners.filters.industry')}</DropdownMenuLabel>
          {(categories.data?.data ?? []).map((category) => {
            const industry = category.code;
            return (
              <DropdownMenuCheckboxItem
                key={industry}
                checked={value.industry.includes(industry)}
                onCheckedChange={() =>
                  onChange({ ...value, industry: toggle(value.industry, industry) })
                }
              >
                {customerCategoryLabel(category, i18n.language)}
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Input
        type="text"
        aria-label={t('partners.filters.industryCode')}
        placeholder={t('partners.filters.industryCode')}
        maxLength={CUSTOMER_INDUSTRY_CODE_MAX_LENGTH}
        value={value.industryCode}
        onChange={(event) => onChange({ ...value, industryCode: event.target.value })}
        className="h-9 w-56"
      />

      {type === 'prospect' ? (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4" aria-hidden="true" />
                {t('partners.filters.leadStatus')}
                {value.leadStatus.length > 0 ? (
                  <Badge variant="secondary">{value.leadStatus.length}</Badge>
                ) : null}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              <DropdownMenuLabel>{t('partners.filters.leadStatus')}</DropdownMenuLabel>
              {LEAD_STATUSES.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={value.leadStatus.includes(status)}
                  onCheckedChange={() =>
                    onChange({ ...value, leadStatus: toggle(value.leadStatus, status) })
                  }
                >
                  {t(`partners.leadStatuses.${status}`)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4" aria-hidden="true" />
                {t('partners.filters.leadSource')}
                {value.leadSource.length > 0 ? (
                  <Badge variant="secondary">{value.leadSource.length}</Badge>
                ) : null}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
              <DropdownMenuLabel>{t('partners.filters.leadSource')}</DropdownMenuLabel>
              {LEAD_SOURCES.map((source) => (
                <DropdownMenuCheckboxItem
                  key={source}
                  checked={value.leadSource.includes(source)}
                  onCheckedChange={() =>
                    onChange({ ...value, leadSource: toggle(value.leadSource, source) })
                  }
                >
                  {t(`partners.leadSources.${source}`)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      ) : null}

      {type === 'supplier' ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4" aria-hidden="true" />
              {t('partners.filters.supplierRating')}
              {value.supplierRating.length > 0 ? (
                <Badge variant="secondary">{value.supplierRating.length}</Badge>
              ) : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>{t('partners.filters.supplierRating')}</DropdownMenuLabel>
            {SUPPLIER_RATINGS.map((rating) => (
              <DropdownMenuCheckboxItem
                key={rating}
                checked={value.supplierRating.includes(rating)}
                onCheckedChange={() =>
                  onChange({ ...value, supplierRating: toggle(value.supplierRating, rating) })
                }
              >
                {t(`partners.supplierRatings.${rating}`)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={availableTags.length === 0}>
            <Filter className="h-4 w-4" aria-hidden="true" />
            {t('partners.filters.tags')}
            {value.tags.length > 0 ? <Badge variant="secondary">{value.tags.length}</Badge> : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
          <DropdownMenuLabel>{t('partners.filters.tags')}</DropdownMenuLabel>
          {availableTags.map((tag) => (
            <DropdownMenuCheckboxItem
              key={tag.value}
              checked={value.tags.includes(tag.value)}
              onCheckedChange={() => onChange({ ...value, tags: toggle(value.tags, tag.value) })}
            >
              {tag.value} ({tag.count})
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4" aria-hidden="true" />
            {t('partners.filters.vatIdStatus')}
            {value.vatIdStatus.length > 0 ? (
              <Badge variant="secondary">{value.vatIdStatus.length}</Badge>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{t('partners.filters.vatIdStatus')}</DropdownMenuLabel>
          {VAT_STATUSES.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={value.vatIdStatus.includes(status)}
              onCheckedChange={() =>
                onChange({ ...value, vatIdStatus: toggle(value.vatIdStatus, status) })
              }
            >
              {t(`partners.vatIdStatuses.${status}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4" aria-hidden="true" />
            {t('partners.filters.riskStatus')}
            {value.riskStatus.length > 0 ? (
              <Badge variant="secondary">{value.riskStatus.length}</Badge>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{t('partners.filters.riskStatus')}</DropdownMenuLabel>
          {PARTNER_RISK_STATUSES.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={value.riskStatus.includes(status)}
              onCheckedChange={() =>
                onChange({ ...value, riskStatus: toggle(value.riskStatus, status) })
              }
            >
              {t(`partners.riskStatuses.${status}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="w-64">
        <CatalogueSelect
          label={t('subdivision.filter')}
          emptyValueLabel={t('subdivision.filter')}
          value={value.subdivisionCode}
          options={subdivisionOptions}
          multiple
          onChange={(subdivisionCode) => onChange({ ...value, subdivisionCode })}
        />
      </div>
      {activeCount > 0 ? (
        <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_PARTNER_FILTERS)}>
          <X className="h-4 w-4" aria-hidden="true" />
          {t('partners.filters.reset')}
        </Button>
      ) : null}
    </div>
  );
}
